import { useCallback, useContext, useEffect, useState } from 'react';

import {
  CellEditingStartedEvent,
  CellValueChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  IRowNode,
} from 'ag-grid-community';
import { Dialog } from 'primereact/dialog';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { FreshShipmentType } from '@/app/slices/freshShipment';
import { TOA_GetFreshShipmentFertList } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import {
  useEstimationHeaderValQuery,
  useFertilizationListQuery,
  useFreshShipmentCacheInvalidation,
  useUpdateDonorScheduleTransaction,
  useUpdateFertTransaction,
} from '@/services/fresh-shipment/hooks';

import { useFertilizationData } from '../hooks/useFertilizationData';
import {
  CONTAMINANT_PRESENT_VALUE,
  D5_VALUE,
  FIELD_NAMES,
  NO_EMBRYOS_PRODUCED_VALUE,
} from '../utils/constants';
import { formatUrlPathToTitle } from '../utils/utils';
import { FreshShipmentCsrGrid } from './FreshShipmentCsrGrid';
import { FreshShipmentIvfViewGrid } from './FreshShipmentIvfViewGrid';
import { FreshShipmentSignedOffGrid } from './FreshShipmentSignedOffGrid';

import './FreshShipmentForm.css';

type FreshShipmentFormProps = {
  className?: string;
  quickSearchValue: string;
  setTotals(totals: {
    qual: number;
    low: number;
    ship: number;
    estCount: number;
    totalSelectedShipped: number;
  }): void;
  signedOffShipmentGroupIds: string[];
  gridRef: React.RefObject<any>;
  csrGridRef: React.RefObject<any>;
  signedOffGridRef: React.RefObject<any>;
};

export const FreshShipmentForm = ({
  className,
  quickSearchValue,
  setTotals,
  signedOffShipmentGroupIds,
  gridRef,
  csrGridRef,
  signedOffGridRef,
}: FreshShipmentFormProps) => {
  const { me } = useAppSelector((state) => state.authentication);
  const { themeMode } = useContext(PrimaryContext);
  const { isCsr, qcLoaded } = useAppSelector(
    (state: { freshShipment: FreshShipmentType }) => state.freshShipment,
  );
  const ivfViewGridRef = gridRef;

  const [params, setParams] = useSearchParams();
  const date = params.get('date');
  const siteId = params.get('sId');
  const id = params.get('group-id');

  const signedOff = signedOffShipmentGroupIds?.includes(id as string);

  const [showQcWarningPopup, setShowQcWarningPopup] = useState(false);

  const { data: fertilizationListData } = useFertilizationListQuery({
    fertilizationDate: date,
    fertilizationSiteId: siteId,
    shipGroupId: id,
  });

  const { data: estimationHeaderVal } = useEstimationHeaderValQuery({
    shipGroupId: id,
  });

  const estimationHeaderValList = estimationHeaderVal;

  const processedFertilizationData = useFertilizationData(
    fertilizationListData,
  );

  const { mutateAsync: updateFertilization } = useUpdateFertTransaction();
  const { mutateAsync: updateDonorSchedule } =
    useUpdateDonorScheduleTransaction();
  const invalidateCache = useFreshShipmentCacheInvalidation();

  useEffect(() => {
    if (window) {
      const url = window.location.pathname;
      const title = formatUrlPathToTitle(url);
      if (title) {
        document.title = title;
      }
    }
  }, []);

  const checkD5 = useCallback(
    (selectedOption: number) => {
      const newParams = new URLSearchParams(params);
      if (
        selectedOption?.toString() === D5_VALUE ||
        document.title.includes('5')
      ) {
        newParams.set('d5', 'true');
      } else {
        newParams.delete('d5');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    checkD5(estimationHeaderValList);
  }, [checkD5, estimationHeaderValList]);

  const calculateTotals = (api: GridApi) => {
    const totals = {
      qual: 0,
      low: 0,
      ship: 0,
      estCount: 0,
      totalSelectedShipped: 0,
    };

    api.forEachNode((node: IRowNode<TOA_GetFreshShipmentFertList>) => {
      const data = node?.data;
      totals.qual = Number(totals.qual) + (Number(data?.predictedGood) || 0);
      totals.low = Number(totals.low) + (Number(data?.predictedVeryLow) || 0);
      totals.ship = Number(totals.ship) + (Number(data?.origShipped) || 0);
      totals.estCount =
        Number(totals.estCount) + (Number(data?.day5esttrans) || 0);
      totals.totalSelectedShipped =
        Number(totals.totalSelectedShipped) +
        (Number(data?.shipped) || 0) +
        (Number(data?.shippedTog) || 0);
    });

    return totals;
  };

  const handleDataUpdated = (params: FirstDataRenderedEvent) => {
    const newTotals = calculateTotals(params.api);
    if (newTotals) {
      setTotals(newTotals);
    }
  };

  const handleUpdateFertilization = (params: CellValueChangedEvent) => {
    const newTotals = calculateTotals(params.api);
    if (newTotals) {
      setTotals(newTotals);
    }

    params.node.setSelected(true);

    let field = params.column.getColDef().field as string;
    let value = params.data[field]?.toString() || '';
    let updatePredictedComments = false;

    field =
      field === FIELD_NAMES.IVF_COMMENT ? FIELD_NAMES.EMBRYO_COMMENT : field;

    if (field === FIELD_NAMES.FRESH_SHIPMENT_COMMENTS && Array.isArray(value)) {
      value = value.join(', ');
      updatePredictedComments = true;
    }

    if (field === FIELD_NAMES.NO_EMBRYOS_PRODUCED) {
      value = value === 'true' ? NO_EMBRYOS_PRODUCED_VALUE : '';
    }
    if (field === FIELD_NAMES.CONTAMINANT_PRESENT) {
      value = value === 'true' ? CONTAMINANT_PRESENT_VALUE : '';
    }

    if (field === FIELD_NAMES.CSR_COMMENT) {
      field = FIELD_NAMES.PREDICTED_COMMENT_ONLY;
    }

    if (
      field === FIELD_NAMES.EMBRYO_COMMENT ||
      field === FIELD_NAMES.FRESH_SHIPMENT_COMMENTS ||
      field === FIELD_NAMES.PREDICTED_COMMENT_ONLY
    ) {
      updatePredictedComments = true;
    }

    if (
      field === FIELD_NAMES.EMBRYO_COMMENT ||
      field === FIELD_NAMES.FRESH_SHIPMENT_COMMENTS ||
      field === FIELD_NAMES.PRIORITY
    ) {
      updateDonorSchedule({
        id: params.data.fertilizationId,
        field,
        value: value?.toString(),
        updatePredictedComments,
        isIvf: params.data.isIvf,
      });
    } else {
      updateFertilization({
        id: params.data.fertilizationId,
        field,
        value: value.toString(),
        updatePredictedComments,
      }).then(() => {
        if (
          field === FIELD_NAMES.CSR_SHIPPED_CLIENT ||
          field === FIELD_NAMES.CSR_SHIPPED_TOG
        ) {
          invalidateCache(['FertilizationListFreshShipment']);
        }
      });
    }
  };

  const handleCellEditingStarted = (params: CellEditingStartedEvent) => {
    if (!qcLoaded) {
      setShowQcWarningPopup(true);
      params.api.stopEditing(true);
      return;
    }
  };

  return (
    <>
      <Dialog
        visible={showQcWarningPopup}
        onHide={() => setShowQcWarningPopup(false)}
        header="QC Information Required"
        modal
      >
        <p>Please enter QC information before editing fields.</p>
      </Dialog>
      {isCsr ? (
        <FreshShipmentCsrGrid
          csrGridRef={csrGridRef}
          className={className as string}
          themeMode={themeMode}
          fertilizations={processedFertilizationData}
          me={me}
          quickSearchValue={quickSearchValue}
          handleUpdateFertilization={handleUpdateFertilization}
          onFirstDataRendered={handleDataUpdated}
        />
      ) : signedOff ? (
        <FreshShipmentSignedOffGrid
          gridRef={signedOffGridRef}
          className={className as string}
          themeMode={themeMode}
          fertilizations={processedFertilizationData}
          me={me}
          quickSearchValue={quickSearchValue}
          onFirstDataRendered={handleDataUpdated}
        />
      ) : (
        <FreshShipmentIvfViewGrid
          className={className as string}
          themeMode={themeMode}
          fertilizations={processedFertilizationData}
          gridRef={ivfViewGridRef}
          me={me}
          quickSearchValue={quickSearchValue}
          handleUpdateFertilization={handleUpdateFertilization}
          handleCellEditingStarted={handleCellEditingStarted}
          onFirstDataRendered={handleDataUpdated}
        />
      )}
    </>
  );
};
